import {
  BMNumberStepper,
  BMPrimitives,
  BMText,
} from "@traveloka/district-bloom";
import IcAdultOccupancy from "@traveloka/icon-kit-web/react/IcAdultOccupancy";
import IcPassengerInfantBaby from "@traveloka/icon-kit-web/react/IcPassengerInfantBaby";
import IcPassengerKid from "@traveloka/icon-kit-web/react/IcPassengerKid";
import { FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import BMView from "../../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../../core/resource/MobileResourceProvider";
import { mobileContent } from "../../../../../core/resource/mobilePayload";
import { useTravelerPickerContextV2 } from "../../../../../providers/TravelerPickerContextV2";
import { IconKitProps } from "../../../../../types";
import { useDisableTravelerPicker } from "../Provider/DisableTravelerPickerContext";
import {
  MAX_FLIGHT_TRAVELER,
  MAX_HOTEL_CHILDREN,
  MAX_HOTEL_TRAVELER,
  MAX_INFANT,
} from "../constants";
import SelectNonEmployeeChildrenAges from "./SelectNonEmployeeChildrenAges";
import { SelectNonEmployeeFormValues } from "./types";

type DisableMaxStepper = {
  adults?: number;
  children?: number;
  infants?: number;
  childrenAges?: number;
};

type Props = {
  name: keyof SelectNonEmployeeFormValues;
  type: "adult" | "child" | "infant" | "childrenAges";
};

const DEFAULT_CHILDREN_AGE = "8";

export default function SelectNonEmployeeStepper({ name, type }: Props) {
  const { CorporateTravelerPicker: cr, CorporateHotelSearchForm: crHotel } =
    useMobileContentResource();

  const { Icon, label } = generateContent(type, cr);
  const { control } = useFormContext<SelectNonEmployeeFormValues>();
  const maxStepperValue = useMaxStepperValue(type);

  return (
    <>
      <BMView style={styles.container} row justify="between" align="center">
        <BMView row align="center" spacing="md">
          <Icon width={24} height={24} />
          <BMText variant="uiLarge">{label}</BMText>
        </BMView>
        <BMView style={styles.stepperContainer}>
          <Controller
            control={control}
            name={name}
            render={({ field: { value, onChange } }) => (
              <BMNumberStepper
                value={
                  name === "childrenAges"
                    ? (value as string[]).length
                    : (value as number)
                }
                onChange={
                  name === "childrenAges"
                    ? (newLength) => {
                        const val = value as string[];
                        if (newLength > val.length) {
                          const diff = newLength - val.length;
                          onChange([
                            ...val,
                            ...Array(diff).fill(DEFAULT_CHILDREN_AGE),
                          ]);
                          return;
                        }
                        onChange(val.slice(0, newLength));
                      }
                    : onChange
                }
                minValue={0}
                maxValue={maxStepperValue}
                testID={`nonemployee-${type}-stepper`}
              />
            )}
          />
        </BMView>
      </BMView>
      {name === "childrenAges" && <SelectNonEmployeeChildrenAges />}
    </>
  );
}

function useMaxStepperValue(type: Props["type"]) {
  const { type: productType } = useTravelerPickerContextV2();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext<SelectNonEmployeeFormValues>();
  const { employees } = useDisableTravelerPicker();
  const [adults, children] = useWatch({
    control,
    name: ["adults", "children"],
  });

  const maxTraveler =
    productType === "flight" ? MAX_FLIGHT_TRAVELER : MAX_HOTEL_TRAVELER;

  const nonEmployeeChildren = children || 0;

  const disableMax: DisableMaxStepper = {
    adults: maxTraveler - (employees.length + nonEmployeeChildren),
    children: MAX_FLIGHT_TRAVELER - (employees.length + adults),
    infants: MAX_INFANT,
    childrenAges: MAX_HOTEL_CHILDREN,
  };
  if (type === "adult" && errors.root?.employees?.type === "400") {
    const adults = getValues("adults");
    disableMax.adults = adults;
  }
  if (type === "child" && errors.root?.employees?.type === "400") {
    const children = getValues("children");
    disableMax.children = children;
  }

  switch (type) {
    case "adult":
      return disableMax.adults;
    case "child":
      return disableMax.children;
    case "infant":
      return disableMax.infants;
    case "childrenAges":
      return disableMax.childrenAges;
    default:
      return undefined;
  }
}

function generateContent(
  type: Props["type"],
  cr: typeof mobileContent.CorporateTravelerPicker
): { Icon: FC<IconKitProps>; label: string } {
  switch (type) {
    case "adult":
      return { Icon: IcAdultOccupancy, label: cr.adultText };
    case "child":
    case "childrenAges":
      return { Icon: IcPassengerKid, label: cr.childText };
    case "infant":
      return { Icon: IcPassengerInfantBaby, label: cr.infantText };
  }
}

const styles = StyleSheet.create({
  container: {
    padding: BMPrimitives.BMSize.md,
  },
  stepperContainer: {
    width: 120,
  },
});
